<template>
  <div class="landing">

    <!-- Üst menü -->
    <header class="nav" :class="{ 'is-open': navOpen }">
      <div class="nav-inner">
        <a class="logo" href="../">
          <img src="../assets/logo.png" alt="Logo">
        </a>

        <button class="burger" @click="navOpen = !navOpen" aria-label="menu">
          <span></span><span></span><span></span>
        </button>

        <nav class="nav-links">
          <a href="#nasil" @click="navOpen = false">{{ $t('landing.navHow') }}</a>
          <a href="#dogrula" @click="navOpen = false">{{ $t('landing.navVerify') }}</a>
          <LanguageSwitcher class="nav-lang" />
          <button class="btn-outline" @click="handleLogin()">
            <span class="icon"><i class="fa fa-sign-in"></i></span>
            <span>{{ $t('landing.login') }}</span>
          </button>
        </nav>
      </div>
    </header>

    <!-- Hero -->
    <section class="hero">
      <div class="hero-inner">
        <h1>{{ $t('landing.heroTitle') }}</h1>
        <p class="hero-sub">{{ $t('landing.heroSubtitle') }}</p>

        <div class="hero-cta">
          <button class="btn-primary is-lg" @click="handleLogin()">
            {{ $t('landing.heroPrimary') }}
          </button>
          <a class="btn-glass is-lg" href="#dogrula">
            <span class="icon"><i class="fa fa-shield"></i></span>
            <span>{{ $t('landing.heroSecondary') }}</span>
          </a>
        </div>

        <!-- Ürünün üç iddiası tek satırda: kaydırmadan görünsün -->
        <ul class="trust">
          <li v-for="(item, i) in $tm('landing.features').slice(3)" :key="i">
            <i class="fa" :class="item.icon"></i>
            <span>{{ item.title }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- Özellikler -->
    <section class="section">
      <div class="wrap">
        <div class="section-head">
          <h2>{{ $t('landing.featuresTitle') }}</h2>
          <p>{{ $t('landing.featuresLead') }}</p>
        </div>

        <div class="grid">
          <article class="feature" v-for="(item, i) in $tm('landing.features')" :key="i">
            <span class="feature-icon"><i class="fa" :class="item.icon"></i></span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.text }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- Nasıl çalışır -->
    <section class="section is-tint" id="nasil">
      <div class="wrap">
        <div class="section-head">
          <h2>{{ $t('landing.howTitle') }}</h2>
        </div>

        <ol class="steps">
          <li v-for="(step, i) in $tm('landing.steps')" :key="i">
            <span class="step-no">{{ i + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.text }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- Doğrulama -->
    <section class="section" id="dogrula">
      <div class="wrap is-narrow">
        <div class="verify-card">
          <span class="verify-badge"><i class="fa fa-shield"></i></span>
          <h2>{{ $t('landing.verifyTitle') }}</h2>
          <p class="verify-lead">{{ $t('landing.verifyLead') }}</p>

          <div class="code-row">
            <input class="input is-code" v-model.trim="code"
                   :placeholder="$t('landing.verifyPlaceholder')"
                   maxlength="40" @keyup.enter="verify">
            <button class="btn-primary" :class="{ 'is-loading': verifying }" @click="verify">
              {{ $t('landing.verifyButton') }}
            </button>
          </div>

          <p class="verify-err" v-if="verifyError">
            <i class="fa fa-exclamation-circle"></i> {{ verifyError }}
          </p>

          <!-- Sonuç -->
          <div class="result" v-if="record">
            <h3>{{ record.subject }}</h3>

            <div class="pills">
              <span class="pill" :class="record.status === 'completed' ? 'is-ok' : 'is-warn'">
                {{ statusLabel(record.status) }}
              </span>
              <span class="pill" :class="record.auditValid ? 'is-ok' : 'is-no'">
                {{ record.auditValid ? $t('landing.chainOk') : $t('landing.chainBroken') }}
              </span>
              <span class="pill" :class="record.digitallySigned ? 'is-info' : 'is-idle'">
                {{ record.digitallySigned ? $t('landing.signedYes') : $t('landing.signedNo') }}
              </span>
            </div>

            <ul class="signers">
              <li v-for="s in record.signers" :key="s.email">
                <div>
                  <strong>{{ s.name }}</strong>
                  <span class="signer-mail">{{ s.email }}</span>
                </div>
                <span class="signer-date" v-if="s.signedAt">{{ $formatDate(s.signedAt) }}</span>
              </li>
            </ul>

            <p class="stamp" v-if="record.timestampedAt">
              <i class="fa fa-clock-o"></i>
              {{ $t('landing.timestampLabel') }}: {{ $formatDate(record.timestampedAt) }}
            </p>
          </div>

          <p class="verify-hint">{{ $t('landing.verifyHint') }}</p>
        </div>
      </div>
    </section>

    <!-- Alt bilgi -->
    <footer class="foot">
      <div class="wrap">
        <p class="foot-credit">{{ $t('landing.footerCredit') }}</p>
        <p class="foot-rights">{{ $t('landing.footerRights') }}</p>
      </div>
    </footer>
  </div>
</template>

<script>

import axios from 'axios'
import Defaults from '../config/Defaults'
import LanguageSwitcher from './LanguageSwitcher.vue'

// Bu sayfa tek basina calisir: api.js, Defaults ve InfoModal'e bagli degil.
// Sunucu adresi apiBase prop'undan geliyor; mevcut yapida
//   <Landing :api-base="Defaults.applications().backEnd.url + '/api'" />
// diyerek besleyebilirsin.

export default {
  name: 'app',
  components: { LanguageSwitcher },
  props: {
    // Doğrulama sorgusunun gideceği adres. Aynı sunucudan servis
    // ediliyorsa varsayılan yeterli.
    // Varsayılan adres Defaults'tan geliyor: frontend ile backend farklı
    // origin'de olduğunda '/api' frontend sunucusuna gider ve 404 döner —
    // arayüz de bunu "belge bulunamadı" diye gösterir.
    apiBase: {
      type: String,
      default: function () {
        try {
          return Defaults.applications().backEnd.url + '/api';
        } catch (e) {
          return '/api';
        }
      }
    }
  },
  data() {
    return {
      navOpen: false,

      code: "",
      verifying: false,
      verifyError: "",
      record: null
    }
  },
  methods: {
    handleLogin() {
      // Store yoksa sayfa yine de acilsin, sadece giris calismasin
      if (!this.$store) {
        this.$emit('login');
        return;
      }
      this.$store.dispatch('login')
        .then(_ => _)
        .catch(errorMessage => {
          console.log(errorMessage)
        })
    },

    verify() {
      const code = this.code.toUpperCase();
      this.verifyError = "";
      this.record = null;

      if (!code) {
        this.verifyError = this.$t('landing.emptyCode');
        return;
      }
      if (this.verifying) return;

      this.verifying = true;

      // Doğrulama açık bir uç: oturum başlığı göndermiyoruz.
      // Sunucu {code:100, record:{...}} veya {code:200, message:'...'} dönüyor.
      axios.get(this.apiBase + '/verify/' + encodeURIComponent(code), { timeout: 15000 })
        .then((response) => {
          const body = response.data || {};
          if (body.code === 100 && body.record) {
            this.record = body.record;
          } else {
            this.verifyError = body.message || this.$t('landing.notFound');
          }
        })
        .catch((error) => {
          // error.response kontrolsüz okunursa ağ koptuğunda sayfa çöker
          if (!error.response) {
            this.verifyError = this.$t('landing.networkError');
          } else if (error.response.status === 404) {
            this.verifyError = this.$t('landing.notFound');
          } else {
            const data = error.response.data;
            this.verifyError = (data && data.message) || this.$t('landing.serverError');
          }
        })
        .then(() => { this.verifying = false; });
    },

    // Durum etiketleri paketler ekranıyla ortak: aynı kavram iki ayrı
    // yerde ayrı çevrilirse er geç birbirinden ayrışıyor
    statusLabel(status) {
      const map = { draft: 'statusDraft', sent: 'statusSent', in_progress: 'statusProgress',
        completed: 'statusCompleted', declined: 'statusDeclined', voided: 'statusVoided' };
      return map[status] ? this.$t('envelopes.' + map[status]) : status;
    }
  }
}
</script>

<style scoped>
.landing {
  background: #fff;
  color: #2b3146;
}

.wrap { max-width: 1060px; margin: 0 auto; padding: 0 22px; }
.wrap.is-narrow { max-width: 720px; }

/* --- Üst menü --- */
.nav {
  position: sticky;
  top: 0;
  z-index: 40;
  background: rgba(24, 24, 45, 0.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.nav-inner {
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 22px;
  height: 62px;
  display: flex;
  align-items: center;
  gap: 18px;
}

.logo { display: flex; align-items: center; }
.logo img { max-height: 30px; display: block; }

.nav-links {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 18px;
}

.nav-links > a {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.72);
  transition: color 0.15s;
}

.nav-links > a:hover { color: #fff; }
.nav-lang { color: #fff; }

.burger {
  display: none;
  flex-direction: column;
  gap: 4px;
  margin-left: auto;
  width: 34px;
  height: 34px;
  padding: 7px 6px;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.burger span { display: block; height: 2px; background: #aeb4cc; border-radius: 2px; }

/* --- Hero --- */
.hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(140deg, #1a1a33 0%, #2b2b55 55%, #363a72 100%);
  padding: 84px 22px 92px;
  text-align: center;
}

/* İki yumuşak ışık: düz degrade bu ölçekte cansız kalıyor */
.hero::before,
.hero::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.hero::before {
  right: -120px;
  top: -140px;
  width: 460px;
  height: 460px;
  background: radial-gradient(circle, rgba(0, 209, 178, 0.26) 0%, rgba(0, 209, 178, 0) 68%);
}

.hero::after {
  left: -140px;
  bottom: -180px;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(104, 122, 255, 0.22) 0%, rgba(104, 122, 255, 0) 70%);
}

.hero-inner { position: relative; z-index: 1; max-width: 760px; margin: 0 auto; }

.hero h1 {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.16;
  letter-spacing: -0.02em;
  color: #fff;
}

.hero-sub {
  font-size: 16.5px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
  margin: 18px auto 0;
  max-width: 620px;
}

.hero-cta {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 11px;
  margin-top: 30px;
}

.trust {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 38px;
}

.trust li {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 999px;
  padding: 7px 15px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.82);
}

.trust i { color: #4fe3c8; font-size: 12px; }

/* --- Bölüm --- */
.section { padding: 78px 0; }
.section.is-tint { background: #f6f7fb; }

.section-head { text-align: center; max-width: 620px; margin: 0 auto 42px; }

.section-head h2 {
  font-size: 27px;
  font-weight: 700;
  color: #1a1e2e;
  letter-spacing: -0.01em;
}

.section-head p { font-size: 15px; color: #79809a; margin-top: 10px; line-height: 1.65; }

/* --- Özellik kartları --- */
.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.feature {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 14px;
  padding: 24px 22px 26px;
  transition: transform 0.16s, box-shadow 0.16s, border-color 0.16s;
}

.feature:hover {
  transform: translateY(-3px);
  border-color: #d5dbe9;
  box-shadow: 0 10px 26px rgba(24, 24, 48, 0.08);
}

.feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(140deg, #e8fbf7 0%, #d8f5ef 100%);
  color: #0b7f6c;
  font-size: 19px;
  margin-bottom: 16px;
}

.feature h3 { font-size: 15.5px; font-weight: 700; color: #1e2233; margin-bottom: 7px; }
.feature p { font-size: 13.5px; color: #79809a; line-height: 1.7; }

/* --- Adımlar --- */
.steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  /* ol varsayilan olarak numara basiyor; numarayi kendimiz rozette
     gosteriyoruz, ikinci bir "1." cikmasin */
  list-style: none;
  margin: 0;
  padding: 0;
}

.steps li { list-style: none; }

.steps li {
  position: relative;
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 14px;
  padding: 22px 20px 24px;
}

/* Kartlar arasındaki ince çizgi akışı gösteriyor */
.steps li:not(:last-child)::after {
  content: "";
  position: absolute;
  right: -16px;
  top: 40px;
  width: 16px;
  height: 1px;
  background: #d8dde9;
}

.step-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #22223d;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 13px;
}

.steps h3 { font-size: 14.5px; font-weight: 700; color: #1e2233; margin-bottom: 6px; }
.steps p { font-size: 13px; color: #79809a; line-height: 1.65; }

/* --- Doğrulama --- */
.verify-card {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 18px;
  padding: 34px 32px 30px;
  box-shadow: 0 14px 40px rgba(24, 24, 48, 0.08);
  text-align: center;
}

.verify-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%);
  color: #06302a;
  font-size: 20px;
  margin-bottom: 16px;
}

.verify-card h2 { font-size: 22px; font-weight: 700; color: #1a1e2e; }
.verify-lead { font-size: 14.5px; color: #79809a; margin: 9px auto 22px; max-width: 480px; line-height: 1.65; }

.code-row { display: flex; gap: 10px; }

.input {
  flex: 1 1 auto;
  min-width: 0;
  height: 48px;
  border: 1px solid #dde1ea;
  border-radius: 10px;
  padding: 0 15px;
  font-size: 15px;
  color: #2b3146;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input.is-code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
}

.input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

.verify-err {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fdeceb;
  color: #a8322c;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 13.5px;
  margin-top: 14px;
}

.result {
  text-align: left;
  border: 1px solid #eef0f6;
  border-radius: 12px;
  padding: 18px;
  margin-top: 18px;
}

.result h3 { font-size: 15.5px; font-weight: 700; color: #1e2233; }

.pills { display: flex; flex-wrap: wrap; gap: 6px; margin: 11px 0 15px; }

.pill {
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 11.5px;
  font-weight: 700;
}

.pill.is-ok { background: #e4f6ef; color: #0f6e5c; }
.pill.is-no { background: #fdeceb; color: #a8322c; }
.pill.is-warn { background: #fdf2e2; color: #a66a12; }
.pill.is-info { background: #e8effc; color: #2d5eaa; }
.pill.is-idle { background: #f2f4fa; color: #7a819a; }

.signers { border-top: 1px solid #f1f3f8; }

.signers li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f8;
}

.signers li:last-child { border-bottom: 0; }
.signers strong { font-size: 13.5px; color: #22283a; display: block; }
.signer-mail { font-size: 12px; color: #8b93a8; }
.signer-date { font-size: 12px; color: #98a0b5; white-space: nowrap; }

.stamp { font-size: 12.5px; color: #79809a; margin-top: 12px; }
.stamp i { margin-right: 5px; opacity: 0.7; }

.verify-hint { font-size: 12.5px; color: #98a0b5; margin-top: 18px; line-height: 1.6; }

/* --- Alt bilgi --- */
.foot {
  background: #1a1a33;
  color: rgba(255, 255, 255, 0.55);
  padding: 34px 0;
  text-align: center;
}

.foot-credit { font-size: 13.5px; color: rgba(255, 255, 255, 0.8); font-weight: 600; }
.foot-rights { font-size: 11.5px; letter-spacing: 0.06em; margin-top: 8px; }

/* --- Düğmeler --- */
.btn-primary,
.btn-glass,
.btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  padding: 11px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, transform 0.12s;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 4px 14px rgba(0, 168, 143, 0.32);
  height: 48px;
}

.btn-primary:hover { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }
.btn-primary.is-loading { opacity: 0.7; pointer-events: none; }
.btn-primary.is-lg { padding: 14px 28px; font-size: 15px; height: auto; }

.btn-glass {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
}

.btn-glass:hover { background: rgba(255, 255, 255, 0.18); }
.btn-glass.is-lg { padding: 14px 26px; font-size: 15px; }

.btn-outline {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  padding: 8px 16px;
  font-size: 13.5px;
}

.btn-outline:hover { background: rgba(255, 255, 255, 0.12); }

/* --- Duyarlı --- */
@media screen and (max-width: 900px) {
  .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .steps li:not(:last-child)::after { display: none; }
  .hero h1 { font-size: 32px; }
}

@media screen and (max-width: 640px) {
  .burger { display: flex; }

  .nav-links {
    display: none;
    position: absolute;
    top: 62px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    background: #1a1a33;
    padding: 18px 22px 22px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .is-open .nav-links { display: flex; }

  .grid, .steps { grid-template-columns: 1fr; }
  .hero { padding: 58px 18px 66px; }
  .hero h1 { font-size: 27px; }
  .section { padding: 54px 0; }
  .verify-card { padding: 26px 18px 22px; }
  .code-row { flex-direction: column; }
}
</style>


// WEBPACK FOOTER //
// src/Components/MainPage.vue